import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { trpc } from '@/api/trpc';
import AlphaAtlasValidationEvidenceWarning from './AlphaAtlasValidationEvidenceWarning';
import { formatPercentMicros as percentMicros } from '@pages/User/AlphaAtlas/alphaAtlasFormat';

type EvidenceState =
    | 'unavailable'
    | 'insufficientSupport'
    | 'notSelected'
    | 'failedVerification'
    | 'validatedHistorical'
    | 'promisingSparse'
    | 'prospectiveConfirmed';

const metric = (value: number | null, digits = 2): string => (value === null ? '—' : value.toFixed(digits));
const ratio = (value: number | null): string => (value === null ? '—' : `${(value * 100).toFixed(1)}%`);
const time = (unix: number | null): string =>
    unix === null
        ? '—'
        : new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short', timeZone: 'America/New_York' }).format(
              unix * 1_000
          );

const AlphaAtlasValidationResearch = () => {
    const [selectedCampaign, setSelectedCampaign] = useState<string | null>(null);
    const [evidenceState, setEvidenceState] = useState<EvidenceState | 'all'>('all');
    const [page, setPage] = useState(1);
    const campaigns = useQuery({
        ...trpc.alphaAtlas.validationCampaigns.queryOptions({ limit: 25 }),
        refetchInterval: 30_000
    });
    const campaignFingerprint = selectedCampaign ?? campaigns.data?.[0]?.campaignFingerprint ?? null;
    const campaign = campaigns.data?.find((item) => item.campaignFingerprint === campaignFingerprint) ?? null;
    const canReadResults = campaign?.status === 'completed' && campaign.resultContentFingerprint !== null;
    const resultInput = useMemo(
        () =>
            campaignFingerprint && canReadResults
                ? {
                      campaignFingerprint,
                      ...(evidenceState === 'all' ? {} : { evidenceState }),
                      page,
                      pageSize: 50
                  }
                : null,
        [campaignFingerprint, canReadResults, evidenceState, page]
    );
    const results = useQuery({
        ...trpc.alphaAtlas.validationResults.queryOptions(resultInput ?? { campaignFingerprint: '0'.repeat(64), page: 1, pageSize: 50 }),
        enabled: resultInput !== null
    });
    const maxPage = results.data ? Math.max(1, Math.ceil(results.data.total / results.data.pageSize)) : 1;

    return (
        <section aria-labelledby="alpha-atlas-validation-heading" className="space-y-5 border-t border-slate-800 pt-8">
            <div className="space-y-2">
                <h2 className="text-2xl font-semibold" id="alpha-atlas-validation-heading">
                    Clean historical validation
                </h2>
                <AlphaAtlasValidationEvidenceWarning />
            </div>

            {campaigns.error && (
                <p className="rounded border border-red-800 p-4 text-red-300" role="alert">
                    {campaigns.error.message}
                </p>
            )}
            <ul aria-label="Validation campaigns" className="grid gap-3 md:grid-cols-2">
                {campaigns.data?.map((item) => {
                    const selected = item.campaignFingerprint === campaignFingerprint;
                    return (
                        <li
                            className={`rounded-xl border p-4 ${
                                selected ? 'border-cyan-500 bg-cyan-950/30' : 'border-slate-800 bg-slate-900'
                            }`}
                            key={item.campaignId}
                        >
                            <button
                                aria-pressed={selected}
                                className="w-full text-left"
                                type="button"
                                onClick={() => {
                                    setSelectedCampaign(item.campaignFingerprint);
                                    setPage(1);
                                }}
                            >
                                <span className="flex flex-wrap items-center justify-between gap-3">
                                    <strong>Campaign {item.campaignId}</strong>
                                    <span className="rounded bg-slate-800 px-2 py-1 text-xs uppercase">{item.status}</span>
                                </span>
                                <span className="mt-2 block text-sm text-slate-400">
                                    Source publication {item.sourcePublicationId} · candidates {item.candidateCount.toLocaleString()} ·
                                    selected K={item.selectedCount}
                                </span>
                                <span className="mt-1 block text-sm text-slate-400">
                                    Result rows {item.resultRowCount.toLocaleString()} · source requests {item.sourceRequestCount}
                                </span>
                                <span className="mt-1 block text-xs text-slate-500">
                                    As of {time(item.asOfAtUnix)} · completed {time(item.completedAtUnix)}
                                </span>
                                <span className="mt-1 block break-all text-xs text-slate-600">{item.campaignFingerprint}</span>
                            </button>
                        </li>
                    );
                })}
            </ul>

            {campaign && (
                <div className="space-y-4">
                    {campaign.status === 'completed' && campaign.selectedCount === 0 && (
                        <p className="rounded border border-cyan-800 bg-cyan-950/20 p-4 text-sm text-cyan-200">
                            K=0: no hypothesis survived the frozen selection policy. Verification was not used to force a candidate through.
                        </p>
                    )}
                    {!canReadResults && (
                        <p className="rounded border border-slate-700 p-4 text-sm text-slate-400">
                            This campaign has no sealed clean validation result artifact yet. Status: {campaign.status}.
                        </p>
                    )}
                    {canReadResults && (
                        <>
                            <label className="block max-w-xs text-sm" htmlFor="alpha-atlas-evidence-state">
                                Evidence state
                                <select
                                    className="mt-1 w-full rounded border border-slate-700 bg-slate-900 p-2"
                                    id="alpha-atlas-evidence-state"
                                    value={evidenceState}
                                    onChange={(event) => {
                                        setEvidenceState(event.target.value as EvidenceState | 'all');
                                        setPage(1);
                                    }}
                                >
                                    <option value="all">All states</option>
                                    <option value="validatedHistorical">Historically validated</option>
                                    <option value="failedVerification">Failed verification</option>
                                    <option value="promisingSparse">Promising, sparse</option>
                                    <option value="notSelected">Not selected</option>
                                    <option value="insufficientSupport">Insufficient support</option>
                                    <option value="unavailable">Unavailable</option>
                                </select>
                            </label>
                            {results.error && (
                                <p className="rounded border border-red-800 p-4 text-red-300" role="alert">
                                    {results.error.message}
                                </p>
                            )}
                            <div className="space-y-3">
                                {results.data?.items.map((result) => (
                                    <article className="rounded-xl border border-slate-800 bg-slate-900 p-4" key={result.rowFingerprint}>
                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                            <h3 className="font-semibold">
                                                Hypothesis {result.hypothesisOrdinal + 1} · {result.scientificWindow}
                                            </h3>
                                            <span className="rounded bg-slate-800 px-2 py-1 text-xs">{result.evidenceState}</span>
                                        </div>
                                        <p className="mt-2 text-sm text-slate-300">
                                            {result.direction} · horizon {result.horizonSeconds.toLocaleString()}s · comparator{' '}
                                            {result.comparatorKind} · {result.selected ? 'selected' : 'not selected'}
                                        </p>
                                        <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
                                            <ResultFact
                                                label="Support"
                                                value={`${result.supportCount} combination / ${result.comparatorSupportCount} comparator`}
                                            />
                                            <ResultFact
                                                label="Net effect"
                                                value={`${percentMicros(result.netMeanMicros)} vs ${percentMicros(
                                                    result.comparatorNetMeanMicros
                                                )}`}
                                            />
                                            <ResultFact label="Effect delta" value={percentMicros(result.effectDeltaMicros)} />
                                            <ResultFact
                                                label="95% interval"
                                                value={`${percentMicros(result.ciLowerMicros)} to ${percentMicros(result.ciUpperMicros)}`}
                                            />
                                            <ResultFact
                                                label="p / family q"
                                                value={`${metric(result.pValue, 4)} / ${metric(result.qValue, 4)}`}
                                            />
                                            <ResultFact
                                                label="Breadth"
                                                value={`${result.distinctEntityCount} entities · ${result.distinctDateCount} dates · ${result.distinctEpisodeCount} episodes`}
                                            />
                                            <ResultFact
                                                label="Max concentration"
                                                value={`${ratio(result.maximumEntityShare)} entity · ${ratio(result.maximumDateShare)} date`}
                                            />
                                            <ResultFact
                                                label="Bootstrap"
                                                value={`${result.bootstrapValidReplicateCount} valid · ${result.bootstrapInvalidReplicateCount} invalid`}
                                            />
                                        </dl>
                                        <details className="mt-3 text-xs text-slate-500">
                                            <summary className="cursor-pointer">Evidence identity and missingness</summary>
                                            <p className="mt-2 break-all">Hypothesis: {result.hypothesisFingerprint}</p>
                                            <p className="break-all">Comparator: {result.comparatorFingerprint}</p>
                                            <p>
                                                Missing: {result.missingCount} combination / {result.comparatorMissingCount} comparator ·
                                                exposure {result.exposureStatus}
                                            </p>
                                            <p>
                                                Window: {time(result.windowStartAtUnix)} – {time(result.windowEndAtUnix)}
                                            </p>
                                        </details>
                                    </article>
                                ))}
                            </div>
                            <nav aria-label="Validation result pages" className="flex items-center justify-between">
                                <button
                                    className="rounded border border-slate-700 px-3 py-2 disabled:opacity-40"
                                    disabled={page <= 1}
                                    type="button"
                                    onClick={() => setPage((value) => Math.max(1, value - 1))}
                                >
                                    Previous
                                </button>
                                <span className="text-sm text-slate-400">
                                    Page {page} of {maxPage}
                                </span>
                                <button
                                    className="rounded border border-slate-700 px-3 py-2 disabled:opacity-40"
                                    disabled={page >= maxPage}
                                    type="button"
                                    onClick={() => setPage((value) => Math.min(maxPage, value + 1))}
                                >
                                    Next
                                </button>
                            </nav>
                        </>
                    )}
                </div>
            )}
        </section>
    );
};

const ResultFact = ({ label, value }: { label: string; value: string }) => (
    <div>
        <dt className="text-slate-500">{label}</dt>
        <dd>{value}</dd>
    </div>
);

export default AlphaAtlasValidationResearch;
